import { FirebaseError } from 'firebase/app';
import {
  collection,
  deleteDoc,
  doc,
  FirestoreError,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, Route, RouteOutlet, TestRoute } from '../../../../global';
import {
  createRoute,
  deleteOutlet,
  setOutlet,
  updateOutlet,
} from '../db/routes';
import { RouteCreateData } from '../db/types';
import { docToJSON, firestore } from '../utils/firebase';
import { usePagination } from '../utils/usePagination';
import { useAuth } from './use-auth';

type RoutesHook = {
  error: FirebaseError;
  hasMore: boolean;
  load: () => void;
  loading: boolean;
  reset: () => void;
  routes: Route[];
};

type RoutesHookOptions = {
  fsaId?: string;
  newRoutes?: boolean;
  pageSize?: number;
};

type RouteUpdateData = {};

export const useRoutes = ({
  fsaId,
  newRoutes,
  pageSize = 10,
}: RoutesHookOptions): RoutesHook => {
  const { id } = useAuth();
  const [routesQuery, setRoutesQuery] = useState<Query>();
  const { error, hasMore, load, loading, reset, values } = usePagination(
    routesQuery,
    pageSize
  );

  useEffect(() => {
    let newQuery: Query;
    if (id) {
      newQuery = query(
        collection(firestore, 'routes'),
        where('userIds', 'array-contains', id),
        orderBy('defaultDayNumber')
      );
      if (fsaId) {
        newQuery = query(newQuery, where('fsa.id', '==', fsaId));
      }
      if (newRoutes) {
        newQuery = query(newQuery, where('newRoute', '==', fsaId));
      }
      setRoutesQuery(newQuery);
    } else if (routesQuery) {
      setRoutesQuery(undefined);
    }
  }, [id, fsaId]);

  useEffect(() => {
    console.log(error);
  }, [error]);

  return useMemo(
    () => ({
      error,
      hasMore,
      load,
      loading,
      reset,
      routes: values ? values.map((v) => docToJSON(v) as TestRoute) : values,
    }),
    [error, hasMore, load, loading, reset, values]
  );
};

export const useRoute = (id?: string) => {
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState<Route | undefined>();
  const [outlets, setOutlets] = useState<RouteOutlet[]>([]);
  const [error, setError] = useState<FirestoreError>();

  useEffect(() => {
    let unsubscribe;
    if (id) {
      setLoading(true);
      const ref = doc(firestore, 'routes', id);
      unsubscribe = onSnapshot(
        ref,
        (doc) => {
          if (doc.exists()) {
            setRoute(docToJSON(doc) as Route);
          } else {
            setRoute(undefined);
          }
          setLoading(false);
        },
        (error: FirestoreError) => {
          setLoading(false);
          setError(error);
        }
      );
    } else {
      setRoute(undefined);
      setLoading(false);
    }
    return unsubscribe;
  }, [id]);

  useEffect(() => {
    loadOutlets();
  }, [id]);

  useEffect(() => {
    console.count('Outlets changed');
  }, [outlets]);

  const create = async (data: RouteCreateData) => {
    try {
      setLoading(true);
      await createRoute(data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(error);
    }
  };

  const update = async (data: RouteUpdateData) => {
    if (id) {
      try {
        setLoading(true);
        const ref = doc(firestore, 'routes', id);
        await updateDoc(ref, data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(err);
      }
    }
  };

  const deleteRoute = async () => {
    if (id) {
      try {
        setLoading(true);
        const ref = doc(firestore, 'routes', id);
        await deleteDoc(ref);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(err);
      }
    }
  };

  const addOutlet = async (data: RouteOutlet) => {
    if (id) {
      try {
        setLoading(true);
        await setOutlet(id, data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(err);
      }
    }
  };

  const removeOutlet = async (outletId: string) => {
    if (id) {
      try {
        setLoading(true);
        await deleteOutlet(id, outletId);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(err);
      }
    }
  };

  const updateWeight = async (outletId: string, weight: number) => {
    if (id) {
      try {
        setLoading(true);
        await updateOutlet(id, outletId, { weight });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(error);
      }
    }
  };

  const loadOutlets = () => {
    if (id) {
      const ref = query(
        collection(firestore, `routes/${id}/routeOutlets`),
        orderBy('weight')
      );
      getDocs(ref)
        .then((snap) =>
          setOutlets(
            snap.docs.map(
              (doc) => ({ id: doc.id, ...doc.data() } as RouteOutlet)
            )
          )
        )
        .catch(setError);
    } else {
      setOutlets([]);
    }
  };

  useEffect(() => {
    console.log(error);
  }, [error]);

  return {
    route,
    error,
    loading,
    create,
    update,
    deleteRoute,
    outlets,
    addOutlet,
    removeOutlet,
    updateWeight,
    loadOutlets,
  };
};
